/**
 * Post-checkout confirmation page.
 *
 * Fetches subscription details from the BE by local subscription uuid
 * (which the BE resolves to CARA's subscription_id, scoped to the
 * caller's company). Returns 404 if the uuid doesn't belong to them.
 *
 * Selection from SubscriptionCheckoutContext is layered on top when
 * available (for the friendly "Plan / Amount / per week" recap), but
 * the canonical billing details (frequency, start/end dates,
 * subscription_id for support reference) come from CARA via the BE
 * fetch — so refreshing the page still shows them.
 */

import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Helmet } from "react-helmet-async";

import { Button } from "@/components/ui/button";
import { useSubscriptionCheckout } from "../../SubscriptionCheckoutContext";
import {
  getSubscriptionOffering,
  type SubscriptionOffering,
} from "@/modules/subscriptions/features/TotalAccess/api/getSubscriptionOffering";
import { getSubscription } from "../../api/getSubscription";
import type { SubscriptionDetail } from "../../api/getSubscription";

function formatPrice(amount: number): string {
  return `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPerLabel(frequency: string): string {
  switch (frequency) {
    case "weekly":
      return "per week";
    case "monthly":
      return "per month";
    case "quarterly":
      return "per quarter";
    case "annual":
    case "yearly":
      return "per year";
    default:
      return `per ${frequency}`;
  }
}

export default function SubscriptionConfirmationPage() {
  const { slug, subscriptionUuid } = useParams<{
    slug: string;
    subscriptionUuid: string;
  }>();

  const { state } = useSubscriptionCheckout();
  const selection = state.selection;

  const [subscription, setSubscription] = useState<SubscriptionDetail | null>(
    null,
  );
  const [offering, setOffering] = useState<SubscriptionOffering | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!subscriptionUuid) {
      setError("Missing subscription identifier.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    const subscriptionRequest = getSubscription(subscriptionUuid);
    // Always fetch the offering on this path — gives us the offering
    // name even when the React context has been wiped (refresh).
    const offeringRequest = slug
      ? getSubscriptionOffering(slug)
      : Promise.resolve(null);

    Promise.all([subscriptionRequest, offeringRequest])
      .then(([subResponse, offeringResponse]) => {
        if (cancelled) return;
        setSubscription(subResponse.data);
        if (offeringResponse) setOffering(offeringResponse.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof Error
            ? e.message
            : "Could not load your subscription details.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [subscriptionUuid, slug]);

  if (loading) {
    return (
      <div
        className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center"
        data-testid="subscription-confirmation-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center space-y-4"
        data-testid="subscription-confirmation-error"
      >
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        <Button asChild variant="outline">
          <Link to={`/subscriptions/${slug ?? "total-access"}`}>Back</Link>
        </Button>
      </div>
    );
  }

  const offeringName =
    selection?.offeringName ?? offering?.name ?? "Subscription";

  return (
    <div
      className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6"
      data-testid="subscription-confirmation"
    >
      <Helmet>
        <title>{offeringName} — Subscribed</title>
      </Helmet>

      <div className="text-center py-6">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
          <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
        </div>
        <h1 className="text-xl font-medium text-gray-900 dark:text-white mb-1">
          You&apos;re subscribed
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Welcome to {offeringName}.
        </p>
      </div>

      <dl className="rounded-lg border border-gray-200 dark:border-slate-700 divide-y divide-gray-200 dark:divide-slate-700">
        <div className="flex justify-between px-4 py-3 text-sm">
          <dt className="text-gray-500 dark:text-gray-400">Plan</dt>
          <dd
            className="text-gray-900 dark:text-white font-medium"
            data-testid="confirmation-offering-name"
          >
            {offeringName}
          </dd>
        </div>

        {subscription ? (
          <div className="flex justify-between px-4 py-3 text-sm">
            <dt className="text-gray-500 dark:text-gray-400">Billing</dt>
            <dd
              className="text-gray-900 dark:text-white capitalize"
              data-testid="confirmation-frequency"
            >
              {selection?.label ?? subscription.frequency}
            </dd>
          </div>
        ) : null}

        {selection ? (
          <div className="flex justify-between px-4 py-3 text-sm">
            <dt className="text-gray-500 dark:text-gray-400">Amount</dt>
            <dd
              className="text-gray-900 dark:text-white"
              data-testid="confirmation-amount"
            >
              {formatPrice(selection.unitPrice)}{" "}
              <span className="text-gray-500 dark:text-gray-400">
                {formatPerLabel(selection.frequency)}
              </span>
            </dd>
          </div>
        ) : null}

        {subscription?.startDate ? (
          <div className="flex justify-between px-4 py-3 text-sm">
            <dt className="text-gray-500 dark:text-gray-400">Starts</dt>
            <dd
              className="text-gray-900 dark:text-white"
              data-testid="confirmation-start-date"
            >
              {subscription.startDate}
            </dd>
          </div>
        ) : null}

        {subscription?.endDate ? (
          <div className="flex justify-between px-4 py-3 text-sm">
            <dt className="text-gray-500 dark:text-gray-400">Ends</dt>
            <dd
              className="text-gray-900 dark:text-white"
              data-testid="confirmation-end-date"
            >
              {subscription.endDate}
            </dd>
          </div>
        ) : null}

        {subscription ? (
          <div className="flex justify-between px-4 py-3 text-sm">
            <dt className="text-gray-500 dark:text-gray-400">
              Subscription ID
            </dt>
            <dd
              className="text-gray-900 dark:text-white font-mono text-xs"
              data-testid="confirmation-subscription-id"
            >
              {subscription.subscriptionId}
            </dd>
          </div>
        ) : null}
      </dl>

      <div className="flex justify-center">
        <Button asChild variant="outline">
          <Link to="/hub">Back to dashboard</Link>
        </Button>
      </div>
    </div>
  );
}
